import indexesService from '@/services/amio/indexes.service'
import fileService from '@/services/amio/file.service'
import previewModule from '@/store/modules/preview.module'
import errorUtils from '@/utils/error.utils'
import toaster from '@/utils/toaster'
import head from 'ramda/src/head'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import { SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { CREATE_BUTTON_LABELS } from '@/utils/text.constants'
import { applyFieldSelection } from '@/utils/xml-source-config.utils'

export const INDEX_CREATE_FORMS = Object.freeze([
  { key: KNOWLEDGE_BASES.XML_FEED, label: 'XML Feed' },
  { key: KNOWLEDGE_BASES.FILE, label: 'File' },
  { key: KNOWLEDGE_BASES.WEB_PAGE, label: 'Web Page' }
])

export function createIndexCreateFormData() {
  return {
    indexSourceName: '',
    webPageSettings: {
      url: '',
      prompt: null,
      format: 'txt',
      headerKey: null,
      headerValue: null
    },
    xmlFeedSettings: {
      url: '',
      item: '',
      itemName: '',
      itemId: '',
      vectorizationSkipTags: [],
      fieldSelection: { embed_fields: [], payload_fields: {}, group_key: [] }
    },
    fileSettings: {
      file: null,
      url: ''
    },
    scheduleParams: null,
    isLoading: false,
    sourceType: KNOWLEDGE_BASES.XML_FEED,
    createButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
    createButtonText: CREATE_BUTTON_LABELS.DEFAULT
  }
}

/**
 * Shared index-creation flow for SettingsIndexCreate and SettingsIndexesAddMarketEditor.
 * Components must implement resolveIndexCreateParams() and may override emitIndexCreated().
 */
export default {
  data() {
    return createIndexCreateFormData()
  },

  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isIndexSourceNameError() {
      return this.errorDescription?.includes('Source Name')
    },

    formsOptions() {
      return INDEX_CREATE_FORMS
    }
  },

  methods: {
    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },

    createIndex(e) {
      e.preventDefault()
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.createIndexWithXMLSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.createIndexWithFileSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.createIndexWithWebPageSource()
      }
    },

    async buildFileSource() {
      const source = { type: KNOWLEDGE_BASES.FILE, name: this.indexSourceName }
      if (this.fileSettings.file) {
        const response = await fileService.create(this.fileSettings.file)
        source.format = response.format
        source.url = response.url
      } else {
        source.format = this.fileSettings.url.split('.').pop()
        source.url = this.fileSettings.url
      }
      return source
    },

    buildWebPageSource() {
      const source = {
        type: 'web_scraping',
        format: 'html',
        name: this.indexSourceName,
        url: this.webPageSettings.url,
        config: {
          prompt: this.webPageSettings.prompt,
          format: this.webPageSettings.format
        }
      }
      if (this.webPageSettings.headerKey !== null || this.webPageSettings.headerValue !== null) {
        source.config.header = {
          key: this.webPageSettings.headerKey,
          value: this.webPageSettings.headerValue
        }
      }
      return source
    },

    buildXmlSource() {
      const { url, item, itemName, itemId, fieldSelection } = this.xmlFeedSettings
      // Qdrant field selection is additive: only carry the keys when the user picked something, shared with the edit flow.
      const config = applyFieldSelection({
        tag_mapping: {
          item: item,
          item_name: itemName,
          item_id: itemId
        }
      }, fieldSelection)
      return {
        type: 'url',
        format: 'xml',
        name: this.indexSourceName,
        config,
        url
      }
    },

    async createIndexWithFileSource() {
      this.isLoading = true
      try {
        const source = await this.buildFileSource()
        await this.finalizeIndexCreate(source)
      } catch (err) {
        this.handleError(err)
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    async createIndexWithWebPageSource() {
      this.isLoading = true
      try {
        await this.finalizeIndexCreate(this.buildWebPageSource())
      } catch (err) {
        this.handleError(err)
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    async createIndexWithXMLSource() {
      this.isLoading = true
      try {
        await this.finalizeIndexCreate(this.buildXmlSource())
      } catch (err) {
        this.handleError(err)
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    async finalizeIndexCreate(source) {
      let createdIndexGroupId = null
      try {
        const { indexName, extraParams, createdIndexGroupId: groupId } = await this.resolveIndexCreateParams()
        createdIndexGroupId = groupId
        const newIndex = await indexesService.create(indexName, [source], this.scheduleParams, extraParams)
        toaster.info('Creating knowledge base... it may take a few minutes.')
        this.error = null
        this.emitIndexCreated(newIndex)
      } catch (err) {
        await this.rollbackIndexCreate?.(createdIndexGroupId)
        this.handleError(err)
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    emitIndexCreated(newIndex) {
      this.$emit('process-new-index', newIndex)
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.createButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.createButtonText = CREATE_BUTTON_LABELS[buttonState]
    },

    handleError(err) {
      if (err.message === 'Payload Too Large') {
        this.error = { description: 'File exceeds 1 MB limit.' }
        return
      }

      const errorMessage = head(err.response?.data?.details?.errors)?.message || err.message
      if (errorUtils.isIndexCreateUpdateError(errorMessage)) {
        this.error = { description: errorMessage }
        return
      }

      this.error = errorMessage
      toaster.error(errorMessage)
    }
  },

  mounted() {
    this.error = null
  }
}
